import { useLayoutEffect, useState } from 'react'
import type { RefObject } from 'react'
import { DEFAULT_GRID_GEOMETRY, measureGridGeometry } from './timeUtils'
import type { GridGeometry } from './timeUtils'

/**
 * The schedule grid's *rendered* row-track sizes, kept in sync with the element.
 *
 * Needed because the engine rounds track lengths to its layout unit, so the
 * requested SLOT_PX isn't what rows actually measure — see the note in
 * [timeUtils]. Re-measures on resize since zoom and root-font changes shift the
 * rounding. Measured in a layout effect so the corrected value is in place
 * before the first paint.
 */
export function useGridGeometry(gridRef: RefObject<HTMLElement | null>): GridGeometry {
  const [geometry, setGeometry] = useState<GridGeometry>(DEFAULT_GRID_GEOMETRY)

  useLayoutEffect(() => {
    const grid = gridRef.current
    if (!grid) return
    const measure = () => {
      const next = measureGridGeometry(grid)
      if (!next) return
      setGeometry(prev =>
        prev.slotPx === next.slotPx && prev.headerPx === next.headerPx ? prev : next,
      )
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(grid)
    return () => observer.disconnect()
  }, [gridRef])

  return geometry
}
